/**
 * thetaDataService.ts — MER Algo Footprints v9.0
 *
 * Market data service. All requests route through MER API's market-data routes,
 * which delegate to the Theta Gateway service via THETA_GATEWAY_URL.
 *
 * Frontend never calls Theta directly. No credentials are handled here.
 *
 * Route prefix: /api/market-data/*
 *   GET /api/market-data/bars/:symbol
 *   GET /api/market-data/quote/:symbol
 *   GET /api/market-data/expirations/:symbol
 *   GET /api/market-data/strikes/:symbol
 *   GET /api/market-data/chain/:symbol
 *
 * ── Graceful degradation ──────────────────────────────────────────────────────
 * When the gateway is not configured or unavailable the backend returns a
 * structured 503 with a `reason` field. This service treats those as
 * "data unavailable" rather than errors — callers receive empty results with
 * a notConfigured flag and no console noise.
 */

import { Candle } from '../types';

// ── Response types ────────────────────────────────────────────────────────────

import { provenanceFrom, type ServedProvenance } from '../shared/providerLabel';
import type { ProviderConfidence } from '../shared/types';

export interface FetchResult {
  candles:        Candle[];
  meta:           Partial<SessionMeta>;
  /**
   * P3 visible provider trust: the governed provenance of the response that
   * produced these candles. A SIBLING of `meta` and never inside it —
   * `SessionMeta` keeps its own numeric contract (`D-P3T-4`). `null` means the
   * response carried none, which is distinct from "Theta served it".
   */
  provenance?:    ServedProvenance | null;
  /**
   * The governed P3-D4 block, taken verbatim off the same served `meta`. The
   * server is authoritative: it is never derived, recomputed or inferred here.
   */
  providerConfidence?: ProviderConfidence | null;
  error?:         string;
  /** true when the gateway is not configured — caller should degrade silently */
  notConfigured?: boolean;
}

export interface OptionsResult {
  options:        import('../types').OptionRow[];
  error?:         string;
  notConfigured?: boolean;
}

interface SessionMeta {
  prevClose?: number;
  prevHigh?:  number;
  prevLow?:   number;
}

// ── Graceful degradation codes ────────────────────────────────────────────────
// When the backend gateway is not configured or unavailable, MER API returns
// a structured error body. Treat these as silent "no data" states.

const GRACEFUL_REASONS = new Set([
  'NOT_CONFIGURED',
  'DISABLED',
  'MISCONFIGURED',
  'UNAVAILABLE',
]);

// ── Market Data Service ───────────────────────────────────────────────────────

async function apiGet(path: string, params: Record<string, string> = {}): Promise<unknown> {
  const qs  = new URLSearchParams(params).toString();
  const url = `/api/market-data${path}${qs ? '?' + qs : ''}`;
  const resp = await fetch(url);

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({})) as
      { reason?: string; error?: string; message?: string };

    // Gateway not configured / unavailable — graceful "no data" state
    if (resp.status === 503 && body.reason && GRACEFUL_REASONS.has(body.reason)) {
      const sentinel = new Error('MARKET_DATA_NOT_CONFIGURED');
      (sentinel as Error & { isNotConfigured: boolean }).isNotConfigured = true;
      throw sentinel;
    }

    throw new Error(body.error ?? body.message ?? `HTTP ${resp.status}`);
  }

  return resp.json();
}

function isNotConfiguredError(e: unknown): boolean {
  return e instanceof Error &&
    (e as Error & { isNotConfigured?: boolean }).isNotConfigured === true;
}

/**
 * Fetch intraday OHLCV candles for a symbol.
 * Routes to /api/market-data/bars/:symbol via MER API → Theta Gateway.
 *
 * Returns { candles: [], notConfigured: true } silently when the gateway is
 * not configured or unavailable — no console noise, no thrown exception.
 */
export async function fetchIntraday(
  symbol:   string,
  interval: '1min' | '5min' | '15min' | '1h' | '4h' | '1D' | '1W',
): Promise<FetchResult> {
  try {
    // Stage 13 (2026-04-26): widened from 2 → 5 trading days.
    // The 2-day window left empty results on weekends and Monday pre-market
    // (no eligible messages in the lookback). 5 trading days guarantees the
    // most recent trading session is included regardless of when the user
    // visits, so the watchlist always has bars to render rather than
    // showing "No data — check market hours."
    // P15 1W (ADR §69): finalized weekly bars need the full governed 30-day
    // window — days=5 would rarely contain even one finalized week.
    const apiResult = await apiGet(`/bars/${encodeURIComponent(symbol)}`, {
      timeframe: interval,
      days:      interval === '1W' ? '30' : '5',
    });

    const data = apiResult as {
      bars?: Array<{ ts: number; open: number; high: number; low: number; close: number; volume: number }>;
      error?: string;
      meta?: unknown;
    };

    // The governed provenance is ALREADY on the served response (`_send` emits
    // `{ ...data, meta }`). It was previously discarded here, which is why the
    // UI had nothing truthful to render and fell back to a Theta literal.
    const provenance = provenanceFrom(data.meta);
    // Same already-served `meta`, same ingest discipline: absent stays absent.
    const providerConfidence =
      (data.meta as { providerConfidence?: ProviderConfidence } | undefined)?.providerConfidence ?? null;

    if (data.error) return { candles: [], meta: {}, provenance, providerConfidence, error: data.error };

    const candles: Candle[] = (data.bars ?? [])
      .map(b => ({
        ts:     b.ts,
        open:   b.open,
        high:   b.high,
        low:    b.low,
        close:  b.close,
        volume: b.volume ?? 0,
      }))
      .filter(c => !isNaN(c.ts) && c.open > 0);

    // Try to get session meta from quote endpoint
    let meta: Partial<SessionMeta> = {};
    try {
      const quoteRaw  = await apiGet(`/quote/${encodeURIComponent(symbol)}`);
      const quoteData = quoteRaw as { bid?: number; ask?: number; mid?: number };
      // Gateway quote does not include session data — meta remains empty for now
      void quoteData;
    } catch (quoteErr) {
      // Quote call failed — not fatal, candles still usable
      if (isNotConfiguredError(quoteErr)) {
        // Gateway not configured — still return candles if we got them
      }
    }

    return { candles, meta, provenance, providerConfidence };

  } catch (e: unknown) {
    if (isNotConfiguredError(e)) {
      return { candles: [], meta: {}, notConfigured: true };
    }
    return { candles: [], meta: {}, error: `Market data: ${e instanceof Error ? e.message : String(e)}` };
  }
}

/**
 * Streaming quote URL — routed through MER API market-data routes.
 * Note: streaming requires the gateway's WebSocket support to be active.
 */
export function getStreamQuoteUrl(symbols: string[]): string {
  return `/api/market-data/subscribe?symbols=${symbols.join(',')}`;
}

/**
 * Streaming barchart URL — routed through MER API market-data routes.
 */
export function getStreamBarchartUrl(symbol: string, interval = '1min'): string {
  return `/api/market-data/subscribe?symbol=${encodeURIComponent(symbol)}&interval=${interval}`;
}
